import { Array, pipe } from 'effect'

import { getRecordNodesWithInnerRowIndex } from '../../../rsheet/util/innerRowRelated'
import type { RSheetColumn, RSheetRecordNode } from '../../column'
import type { CellGroup, CopyMatrix } from '../../copyMatrix'
import type { RSheetsCursor } from '../../cursor/cursorTypes'
import { flattenCopyMatrix, type FlattenedCopyMatrix } from './flattenCopyMatrix'

// コピーした行列を、貼り付け先で扱える構造に組み替える
export const rebuildCopyMatrixForPasteTarget = ({
  copyMatrix,
  cursor,
  records,
  targetColumns,
  pseudoRootColumn,
}: {
  copyMatrix: CopyMatrix
  cursor: RSheetsCursor
  records: RSheetRecordNode[]
  targetColumns: Array<RSheetColumn<RSheetRecordNode>>
  pseudoRootColumn: RSheetColumn<RSheetRecordNode>
}): CopyMatrix => {
  const flattenedCopyMatrix = flattenCopyMatrix(copyMatrix)
  const rowSizes = computeRowSizesToMatchDestination({
    totalRows: flattenedCopyMatrix.rows.length,
    startRowIndex: cursor.main.rowIndex,
    startInnerRowIndex: cursor.main.innerRowIndex,
    records,
    pseudoRootColumn,
  })

  const groupedRowsBySize = pipe(
    rowSizes,
    Array.reduce(
      {
        offset: 0,
        groupedRowsBySize: [] as Array<FlattenedCopyMatrix['rows']>,
      },
      (state, rowSize) => {
        const nextOffset = state.offset + rowSize
        return {
          offset: nextOffset,
          groupedRowsBySize: [...state.groupedRowsBySize, flattenedCopyMatrix.rows.slice(state.offset, nextOffset)],
        }
      },
    ),
    (state) => state.groupedRowsBySize,
  )

  return {
    rows: pipe(
      groupedRowsBySize,
      Array.map((groupedRows) => ({
        columns: pipe(
          targetColumns,
          Array.map((targetColumn, columnIndex) => {
            const cellGroup = getCellGroupForColumn({
              rows: groupedRows,
              columnIndex,
            })
            const cellsPerParent = computeCellsPerParentForTargetColumn({
              targetColumn,
              pseudoRootPath: pseudoRootColumn.node.path,
              cellCount: cellGroup.length,
            })

            return {
              cellGroup,
              cellsPerParent,
            }
          }),
        ),
      })),
    ),
  }
}

// あるパスが指定された祖先パスの配下にあるかを判定する。
const isDescendantPath = (path: string[], ancestorPath: string[]): boolean =>
  ancestorPath.length <= path.length &&
  pipe(
    ancestorPath,
    Array.zip(path),
    Array.every(([ancestorSegment, pathSegment]) => ancestorSegment === pathSegment),
  )

// 疑似ルート列に対応する貼り付け可能な inner row の区間を取得する。
const getSlotsForPseudoRootNode = ({
  record,
  pseudoRootColumn,
  startInnerRowIndex,
}: {
  record: RSheetRecordNode | undefined
  pseudoRootColumn: RSheetColumn<RSheetRecordNode>
  startInnerRowIndex: number
}): Array<{ innerRowIndexStart: number; innerRowIndexEnd: number }> => {
  if (record === undefined) {
    return [
      {
        innerRowIndexStart: startInnerRowIndex,
        innerRowIndexEnd: startInnerRowIndex + 1,
      },
    ]
  }

  const slots = pipe(
    getRecordNodesWithInnerRowIndex(record, pseudoRootColumn.node),
    Array.filter((node) => !(node.parentRecordNode === undefined && node.recordNode === undefined)),
    Array.filter((node) => node.innerRowIndexEnd > startInnerRowIndex),
    Array.map((node) => ({
      innerRowIndexStart: Math.max(node.innerRowIndexStart, startInnerRowIndex),
      innerRowIndexEnd: node.innerRowIndexEnd,
    })),
  )

  if (slots.length > 0) {
    return slots
  }

  const fallbackInnerRowIndexEnd = Math.max(startInnerRowIndex + 1, record.meta.innerRowIndexEnd)
  return [
    {
      innerRowIndexStart: startInnerRowIndex,
      innerRowIndexEnd: fallbackInnerRowIndexEnd,
    },
  ]
}

// 貼り付け先レコードの構造に合わせて、各行グループが何行分のコピー結果を消費するかを計算する。
const computeRowSizesToMatchDestination = ({
  totalRows,
  startRowIndex,
  startInnerRowIndex,
  records,
  pseudoRootColumn,
}: {
  totalRows: number
  startRowIndex: number
  startInnerRowIndex: number
  records: RSheetRecordNode[]
  pseudoRootColumn: RSheetColumn<RSheetRecordNode>
}): number[] => {
  if (totalRows <= 0) {
    return []
  }

  interface RowSizeState {
    remainingRows: number
    rowIndex: number
    currentStartInnerRowIndex: number
    rowSizes: number[]
  }

  const initialState: RowSizeState = {
    remainingRows: totalRows,
    rowIndex: startRowIndex,
    currentStartInnerRowIndex: startInnerRowIndex,
    rowSizes: [],
  }

  Array.forEach(Array.range(0, totalRows - 1), () => {
    if (initialState.remainingRows <= 0) {
      return
    }

    const slots = getSlotsForPseudoRootNode({
      record: records[initialState.rowIndex],
      pseudoRootColumn,
      startInnerRowIndex: initialState.currentStartInnerRowIndex,
    })

    const availableRows = Math.max(1, slots.length)
    const rowSize = Math.min(initialState.remainingRows, availableRows)
    initialState.remainingRows -= rowSize
    initialState.rowIndex += 1
    initialState.currentStartInnerRowIndex = 0
    initialState.rowSizes.push(rowSize)
  })

  return initialState.rowSizes
}

// 指定した列インデックスについて、複数行にまたがる cellGroup を 1 つに集約する。
const getCellGroupForColumn = ({
  rows,
  columnIndex,
}: {
  rows: FlattenedCopyMatrix['rows']
  columnIndex: number
}): CellGroup =>
  pipe(
    rows,
    // セルは不変なのでそのまま引き継ぐ（salescoreValue 等を落とさない）
    // eslint-disable-next-line security/detect-object-injection -- columnIndex is a safe loop counter
    Array.flatMap((row) => row.columns[columnIndex]?.cellGroup ?? []),
  )

// 貼り付け先列が疑似ルートの子かどうかに応じて cellsPerParent を組み立てる。
const computeCellsPerParentForTargetColumn = ({
  targetColumn,
  pseudoRootPath,
  cellCount,
}: {
  targetColumn: RSheetColumn<RSheetRecordNode>
  pseudoRootPath: string[]
  cellCount: number
}): number[] => {
  const targetColumnPath = targetColumn.node.path
  const isChildOfPseudoRoot =
    targetColumnPath.length > pseudoRootPath.length && isDescendantPath(targetColumnPath, pseudoRootPath)

  if (!isChildOfPseudoRoot) {
    return [cellCount]
  }

  return pipe(
    Array.range(0, cellCount - 1),
    Array.map(() => 1),
  )
}
